import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  useGetScheduleChart,
  usePutRescheduleWorkOrder,
} from "../../hooks/react-query/useScheduleData";
import { getUserLocation } from "../../redux/auth";

import { SchedulerDragDrop } from "../SchedulerDragDrop";
import ScheduleDates from "./ScheduleDates";
import ScheduleGraph from "./ScheduleGraph";
import ScheduleGridLines from "./ScheduleGridLines";
import SchedulerGoals from "./SchedulerGoals";

import {
  VIEW_HOURS,
  getWeekGoal,
  getDailyGoal,
  getValue,
  getCeiling,
  getSuggestions,
  getTargetDate,
  getTargetStartDate,
} from "./helper";
import { StyledGraphStack, StyledHubCardStack } from "./styles";

export const ScheduleBarChart = () => {
  const defaultLocation = useSelector(getUserLocation);
  const { search } = useLocation();
  const { data: chartData = [], refetch } = useGetScheduleChart({
    defaultLocation,
  });
  const { mutate: reschedule } = usePutRescheduleWorkOrder();

  useEffect(() => {
    refetch();
  }, [search?.toString()]);

  const [view, setView] = useState(VIEW_HOURS);
  const [showLabels, setShowLabels] = useState(false);
  const [suggestedIds, setSuggestedIds] = useState(null);
  const [suggestedState, setSuggestedState] = useState(null);
  const [dragUpdate, setDragUpdate] = useState(null);

  const weeks = useMemo(() => {
    const source = suggestedState || chartData || [];
    return source.slice(0, 6);
  }, [suggestedState, chartData]);

  const ceiling = getCeiling(weeks, view);
  const heightFactor = (100 / ceiling) * 100;

  const draggableElements = useMemo(
    () =>
      weeks.map((week) => {
        const [weeklyGoal] = getWeekGoal(week, view) || 1;
        const [_, dailyGoal] = getDailyGoal(week, view) || 1;

        return {
          ...week,
          weeklyHourGoal: week.waxHourGoal,
          weeklyRevenueGoal: week.revenueGoal,
          view,
          ceiling,
          elements: week.elements.map((e) => {
            const [value1, value2] = getValue(e, view);
            const raw = Math.min(Math.max(value2 / dailyGoal, 0), 1);
            const ramped = Math.sqrt(raw) * 0.8 + 0.2;
            return {
              ...e,
              weeklyHourGoal: week.waxHourGoal,
              weeklyRevenueGoal: week.revenueGoal,
              view,
              showLabels,
              suggested: suggestedIds?.has(String(e.id)) || false,
              opacity: Math.round(ramped * 100) / 100,
              draggableProps: {
                sx: { height: `${(value1 / weeklyGoal) * heightFactor}%` },
              },
            };
          }),
        };
      }),
    [weeks, view, showLabels, suggestedIds, ceiling, heightFactor],
  );

  const handleChange = useCallback(
    (newState, { draggableId } = {}) => {
      if (!draggableId || !dragUpdate?.destination) return;

      const targetDate = getTargetStartDate(weeks, dragUpdate);
      if (!targetDate) return;

      const draggedWo = weeks
        .flatMap((w) => w.elements)
        .find((e) => String(e.id) === String(draggableId));

      if (!draggedWo?.workOrder) return;

      const sameDate =
        draggedWo.startDate &&
        new Date(draggedWo.startDate).toDateString() ===
          new Date(targetDate).toDateString();
      if (sameDate) return;

      reschedule({
        id: draggedWo.workOrder,
        startDate: targetDate,
        toId: String(dragUpdate.destination.droppableId),
        toIndex: dragUpdate.destination.index,
      });
    },
    [weeks, dragUpdate, reschedule],
  );

  const handleDragUpdate = useCallback((update) => {
    setDragUpdate(update);
  }, []);

  const targetDate = useMemo(
    () => getTargetDate(weeks, dragUpdate),
    [weeks, dragUpdate],
  );

  const handleSuggestReschedule = useCallback(() => {
    if (suggestedIds) {
      setSuggestedIds(null);
      setSuggestedState(null);
    } else {
      const { state, movedIds } = getSuggestions(weeks);
      setSuggestedIds(movedIds);
      setSuggestedState(state);
    }
  }, [suggestedIds, weeks]);

  return (
    <StyledHubCardStack gap="20px">
      <SchedulerGoals
        view={view}
        setView={setView}
        showLabels={showLabels}
        onToggleLabels={() => setShowLabels((p) => !p)}
        showSuggestions={!!suggestedIds}
        onSuggestReschedule={handleSuggestReschedule}
      />
      <StyledGraphStack>
        <ScheduleGraph ceiling={ceiling} />
        <ScheduleGridLines ceiling={ceiling} />
        <Stack gap="5px" width="100%">
          <SchedulerDragDrop
            elements={draggableElements}
            view={view}
            targetDate={targetDate}
            onChange={handleChange}
            onDragUpdate={handleDragUpdate}
          />
          <ScheduleDates elements={weeks} />
        </Stack>
      </StyledGraphStack>
    </StyledHubCardStack>
  );
};

export default ScheduleBarChart;
